import React, { useContext } from 'react'
import { Tenant } from '../../Interfaces/Types'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon/Icon'
import { itemColor } from '../../Utilities/helpers/helpers'
import { useTenants } from '../Context/TenantContext'
import { AuthRoleContext } from '../Auth/AuthRoleContext'
import { useSystemStatus } from '../Context/SystemStatusContext'
import { useApi } from '../../Hooks'

interface QuickLaunchButtonProps {
  tenant: Tenant
}

const QuickLaunchButton: React.FC<QuickLaunchButtonProps> = ({ tenant }) => {
  const roles = useContext(AuthRoleContext)
  const { selectedTenant, setTenantDeployStatus, revertTenantDeployStatus } = useTenants()
  const { reservedEnvironments, scheduledEnvironments } = useSystemStatus()
  const { fetchApi } = useApi()

  // Stage reserved for a rollout → no manual quick-launch. A scheduled (not yet
  // running) rollout is called out separately so the reason is clear.
  const tenantEnv = (tenant.environment ?? '').toLowerCase()
  const isReserved = reservedEnvironments.some((env) => env.toLowerCase() === tenantEnv)
  const isScheduled = scheduledEnvironments.some((env) => env.toLowerCase() === tenantEnv)
  const reservedTooltip = isScheduled
    ? 'A rollout is scheduled for this stage: manual runs are disabled'
    : 'Stage is in a rollout: manual runs are disabled'

  const handleQuickLaunch = (tenantId: string) => {
    // Optimistically flip to 'updating' so the whole UI reacts instantly;
    // rolled back if the launch request fails.
    setTenantDeployStatus(tenantId, 'updating')
    fetchApi({
      endpoint: `/api/tenantsconfiguration/${tenantId}/launch`,
      method: 'PUT',
      body: {}
    }).catch((error: any) => {
      revertTenantDeployStatus(tenantId)
      console.error(error)
    })
  }

  if (tenant.deployStatus === 'finished' && roles?.isAdmin) {
    return (
      <Button2
        buttonSize='30px'
        style={{ borderRadius: '8px' }}
        leftElement={<Icon iconName='SpaceRocketFlying' size={14} color={isReserved ? 'var(--tma-text-faint)' : 'var(--tma-accent)'} />}
        tabIndex={-1}
        text=''
        onClick={() => !isReserved && handleQuickLaunch(tenant.id)}
        isDisabled={isReserved}
        tooltip={isReserved ? reservedTooltip : 'Quick run this tenant with the same configuration'}
        type='button'
        variant='outlined'
      />
    )
  }

  if (tenant.deployStatus === 'updating') {
    return (
      <Icon
        color={itemColor(tenant, tenant.id === selectedTenant?.id)}
        iconName='EmailActionSync1'
        size={16}
        style={{ margin: '0 9px 0 0 ', animation: 'spin 1s linear infinite' }}
      />
    )
  }

  return (
    <Icon color={itemColor(tenant, tenant.id === selectedTenant?.id)} iconName='InProgress' size={16} style={{ margin: '0 9px 0 0 ' }} />
  )
}

export default QuickLaunchButton
